//M10-13811
import { useEffect, useState } from "react";
import { Button, Modal } from "src/components";

interface OrderSelectDialogProps {
    isOpen: boolean;
    orderCodes: string[];
    onClose: () => void;
    onSelect: (orderCode: string) => void;
}

const OrderSelectDialog = ({ isOpen, orderCodes, onClose, onSelect }: OrderSelectDialogProps) => {
    const [selected, setSelected] = useState<string>("");

    // Reset selection whenever the dialog re-opens with a fresh list.
    useEffect(() => {
        if (isOpen) setSelected("");
    }, [isOpen]);

    const handleConfirm = () => {
        if (!selected) return;
        onSelect(selected);
    };

    return (
        <Modal isOpen={isOpen} isHeader={false} onCloseModal={onClose} backdropClose>
            <div className="w-[480px] max-w-full p-6 text-left">
                {/* Header */}
                <div className="mb-5">
                    <div className="flex items-center gap-2 mb-1">
                        <svg
                            className="w-5 h-5 text-indigo-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-800">Select an Order</h2>
                    </div>
                    <p className="text-sm text-gray-600">
                        This PO is linked to{" "}
                        <span className="font-semibold text-gray-800">{orderCodes.length}</span> orders.
                        Choose the one you want to edit.
                    </p>
                </div>

                {/* Selection counter */}
                {selected && (
                    <div className="mb-3 p-2.5 bg-indigo-50 border border-indigo-200 rounded-md">
                        <p className="text-sm text-indigo-800">
                            Selected: <span className="font-semibold">{selected}</span>
                        </p>
                    </div>
                )}

                {/* Order list */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-[320px] overflow-y-auto divide-y divide-gray-100">
                        {orderCodes.map((code) => {
                            const isSelected = selected === code;
                            return (
                                <label
                                    key={code}
                                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                                        isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="order-select"
                                        value={code}
                                        checked={isSelected}
                                        onChange={() => setSelected(code)}
                                        className="w-4 h-4 accent-indigo-600"
                                    />
                                    <span
                                        className={`text-sm font-medium ${
                                            isSelected ? "text-indigo-900" : "text-gray-700"
                                        }`}
                                    >
                                        {code}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-end gap-3">
                    <Button
                        onClick={onClose}
                        className="min-w-[110px] px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!selected}
                        className={`min-w-[110px] px-4 py-2 rounded-lg transition-colors shadow-sm ${
                            selected
                                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                        Open Order
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default OrderSelectDialog;
//M10-13811